import React, { useRef, useState } from "react";
import Card from "./Card.js";
import { getNetlifyFunctionsBase } from "../../utils/netlifyFunctions.js";
import { isValidEmail } from "../../utils/emailValidation.js";
import {
  CLIENT_POPULATIONS,
  EXPERIENCE_OPTIONS,
  FEEDBACK_OPTIONS,
  FORM_STEPS,
  PROVIDER_BETA_SUCCESS_MESSAGE,
  PROVIDER_ROLES,
  USEFULNESS_OPTIONS
} from "../../constants/providerBeta.js";

const initialForm = {
  fullName: "",
  email: "",
  country: "",
  role: "",
  experience: "",
  populations: [],
  usefulness: "",
  currentReflection: "",
  concerns: "",
  sessionInfo: "",
  feedbackOpenness: "",
  futureDiscussions: false
};

const MultiStepForm = () => {
  const honeypotRef = useRef(null);
  const errorRef = useRef(null);
  const submittingRef = useRef(false);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const togglePopulation = (value) => {
    setForm((prev) => {
      const has = prev.populations.includes(value);
      return {
        ...prev,
        populations: has
          ? prev.populations.filter((p) => p !== value)
          : [...prev.populations, value]
      };
    });
  };

  const showError = (message) => {
    setStatus({ type: "error", message });
    requestAnimationFrame(() => {
      errorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  };

  const validateStep = (index) => {
    if (index === 0) {
      if (!form.fullName.trim()) return "Full name is required.";
      if (!isValidEmail(form.email)) return "A valid email is required.";
      if (!form.country.trim()) return "Country is required.";
    }
    if (index === 1) {
      if (!form.role) return "Please select your professional role.";
      if (!form.experience) return "Please select years of experience.";
    }
    if (index === 2) {
      if (!form.populations.length) return "Select at least one client population.";
    }
    if (index === 3) {
      if (!form.usefulness) return "Please indicate how useful a reflection tool would be.";
    }
    if (index === 4) {
      if (!form.feedbackOpenness) return "Please indicate your openness to providing feedback.";
    }
    return "";
  };

  const goNext = () => {
    const err = validateStep(step);
    if (err) {
      showError(err);
      return;
    }
    setStatus({ type: "", message: "" });
    setStep((s) => Math.min(s + 1, FORM_STEPS.length - 1));
  };

  const goBack = () => {
    setStatus({ type: "", message: "" });
    setStep((s) => Math.max(s - 1, 0));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submittingRef.current || submitting) return;

    const err = validateStep(step);
    if (err) {
      showError(err);
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    setStatus({ type: "", message: "" });

    try {
      const res = await fetch(`${getNetlifyFunctionsBase()}/.netlify/functions/submitProviderBeta`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          honeypot: honeypotRef.current?.value || "",
          ...form,
          fullName: form.fullName.trim(),
          email: form.email.trim().toLowerCase(),
          country: form.country.trim(),
          currentReflection: form.currentReflection.trim(),
          concerns: form.concerns.trim(),
          sessionInfo: form.sessionInfo.trim()
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Something went wrong. Please try again.");
      }
      setSubmitted(true);
      setStatus({
        type: "success",
        message: PROVIDER_BETA_SUCCESS_MESSAGE
      });
    } catch (submitErr) {
      showError(submitErr?.message || "Submission failed.");
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Card className="provider-beta__form-card provider-beta__form-card--success">
        <h2 className="provider-beta__form-title">Thank you</h2>
        <p className="provider-beta__form-lead">{status.message}</p>
      </Card>
    );
  }

  return (
    <Card className="provider-beta__form-card" as="form" onSubmit={handleSubmit} noValidate>
      <input
        ref={honeypotRef}
        type="text"
        name="company"
        className="provider-beta__hp"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />

      <div className="provider-beta__form-header">
        <h2 className="provider-beta__form-title">Join Provider Beta</h2>
        <p className="provider-beta__form-step-label">
          Step {step + 1} of {FORM_STEPS.length} — {FORM_STEPS[step]}
        </p>
        <div
          className="provider-beta__progress"
          role="progressbar"
          aria-valuenow={step + 1}
          aria-valuemin={1}
          aria-valuemax={FORM_STEPS.length}
          aria-label={`Form progress: step ${step + 1} of ${FORM_STEPS.length}`}
        >
          <div
            className="provider-beta__progress-fill"
            style={{ width: `${((step + 1) / FORM_STEPS.length) * 100}%` }}
          />
        </div>
      </div>

      {status.type === "error" ? (
        <p ref={errorRef} className="provider-beta__alert provider-beta__alert--error" role="alert">
          {status.message}
        </p>
      ) : null}

      <fieldset className="provider-beta__fieldset" disabled={submitting}>
        {step === 0 ? (
          <>
            <div className="provider-beta__field">
              <label htmlFor="pb-fullName">Full Name</label>
              <input
                id="pb-fullName"
                type="text"
                autoComplete="name"
                value={form.fullName}
                onChange={(e) => update("fullName", e.target.value)}
                required
              />
            </div>
            <div className="provider-beta__field">
              <label htmlFor="pb-email">Email</label>
              <input
                id="pb-email"
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                required
              />
            </div>
            <div className="provider-beta__field">
              <label htmlFor="pb-country">Country</label>
              <input
                id="pb-country"
                type="text"
                autoComplete="country-name"
                value={form.country}
                onChange={(e) => update("country", e.target.value)}
                required
              />
            </div>
          </>
        ) : null}

        {step === 1 ? (
          <>
            <div className="provider-beta__field">
              <label htmlFor="pb-role">Professional role</label>
              <select id="pb-role" value={form.role} onChange={(e) => update("role", e.target.value)} required>
                <option value="">Select one</option>
                {PROVIDER_ROLES.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </div>
            <fieldset className="provider-beta__radio-group">
              <legend>Years of experience</legend>
              {EXPERIENCE_OPTIONS.map((opt) => (
                <label key={opt} className="provider-beta__radio">
                  <input
                    type="radio"
                    name="experience"
                    value={opt}
                    checked={form.experience === opt}
                    onChange={(e) => update("experience", e.target.value)}
                  />
                  {opt}
                </label>
              ))}
            </fieldset>
          </>
        ) : null}

        {step === 2 ? (
          <fieldset className="provider-beta__checkbox-group">
            <legend>Client population (select all that apply)</legend>
            <div className="provider-beta__checkbox-grid">
              {CLIENT_POPULATIONS.map((pop) => (
                <label key={pop} className="provider-beta__checkbox">
                  <input
                    type="checkbox"
                    checked={form.populations.includes(pop)}
                    onChange={() => togglePopulation(pop)}
                  />
                  {pop}
                </label>
              ))}
            </div>
          </fieldset>
        ) : null}

        {step === 3 ? (
          <>
            <fieldset className="provider-beta__radio-group">
              <legend>Would a structured reflection tool be useful between sessions?</legend>
              {USEFULNESS_OPTIONS.map((opt) => (
                <label key={opt} className="provider-beta__radio">
                  <input
                    type="radio"
                    name="usefulness"
                    value={opt}
                    checked={form.usefulness === opt}
                    onChange={(e) => update("usefulness", e.target.value)}
                  />
                  {opt}
                </label>
              ))}
            </fieldset>
            <div className="provider-beta__field">
              <label htmlFor="pb-currentReflection">
                How do your clients currently reflect between sessions?
              </label>
              <textarea
                id="pb-currentReflection"
                rows={4}
                value={form.currentReflection}
                onChange={(e) => update("currentReflection", e.target.value)}
              />
            </div>
            <div className="provider-beta__field">
              <label htmlFor="pb-concerns">
                What concerns would you have about clients using this type of tool?
              </label>
              <textarea
                id="pb-concerns"
                rows={4}
                value={form.concerns}
                onChange={(e) => update("concerns", e.target.value)}
              />
            </div>
            <div className="provider-beta__field">
              <label htmlFor="pb-sessionInfo">
                What information would you want clients to bring into sessions?
              </label>
              <textarea
                id="pb-sessionInfo"
                rows={4}
                value={form.sessionInfo}
                onChange={(e) => update("sessionInfo", e.target.value)}
              />
            </div>
          </>
        ) : null}

        {step === 4 ? (
          <>
            <fieldset className="provider-beta__radio-group">
              <legend>Would you be open to providing feedback after trying the beta?</legend>
              {FEEDBACK_OPTIONS.map((opt) => (
                <label key={opt} className="provider-beta__radio">
                  <input
                    type="radio"
                    name="feedbackOpenness"
                    value={opt}
                    checked={form.feedbackOpenness === opt}
                    onChange={(e) => update("feedbackOpenness", e.target.value)}
                  />
                  {opt}
                </label>
              ))}
            </fieldset>
            <label className="provider-beta__checkbox provider-beta__checkbox--solo">
              <input
                type="checkbox"
                checked={form.futureDiscussions}
                onChange={(e) => update("futureDiscussions", e.target.checked)}
              />
              I would like to participate in future provider discussions.
            </label>
          </>
        ) : null}
      </fieldset>

      <div className="provider-beta__form-actions">
        {step > 0 ? (
          <button type="button" className="provider-beta__secondary-button" onClick={goBack} disabled={submitting}>
            Back
          </button>
        ) : (
          <span />
        )}
        {step < FORM_STEPS.length - 1 ? (
          <button type="button" className="primary-button" onClick={goNext}>
            Continue
          </button>
        ) : (
          <button type="submit" className="primary-button" disabled={submitting}>
            {submitting ? "Submitting…" : "Join Provider Beta"}
          </button>
        )}
      </div>
    </Card>
  );
};

export default MultiStepForm;
